import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import type  { Collars } from "../../../../types/Collars";
import CollarModal from './CollarModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const CollarsPage: React.FC = () => {
  const [collars, setCollars] = useState<Collars[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Collars | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCollars = async () => {
    try {
      const data = await apiRequest<Collars[]>({
        method: 'get',
        url: 'collars',
      });
      setCollars(data);
    } catch (error) {
      showError('Failed to fetch collar');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCollars();
  }, []);

  const handleEditClick = (record: Collars) => {
    setIsEditMode(true);
    setInitialValues({
      collar_id: record.collar_id,
      collar_name: record.collar_name,
      collar_mesh_name: record.collar_mesh_name,
      collar_status: record.collar_status,
      collar_image: record.collar_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCollars();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (collar_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-collar/${collar_id}`,
      });
      showSuccess('collar deleted');
      fetchCollars();
    } catch {
      showError('Failed to delete collar');
    }
  };

  const handleStatusToggle = async (collarId: number) => {
    setCollars((prev) =>
      prev.map((item) =>
        item.collar_id === collarId
          ? { ...item, collar_status: item.collar_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-collar/${collarId}`,
      });
      showSuccess('collar status updated');
    } catch (error) {
      showError('Failed to update status');
      setCollars((prev) =>
        prev.map((item) =>
          item.collar_id === collarId
            ? { ...item, collar_status: item.collar_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Collars>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Collars> = [
    
    {
      title: 'collar',
      dataIndex: 'collar_name',
      key: 'collar_name',
      filters: Array.from(new Set(collars.map((s) => s.collar_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.collar_name || null,
      onFilter: (value, record) => record.collar_name.includes(value as string),
      sorter: (a, b) => a.collar_name.localeCompare(b.collar_name),
      sortOrder: sortedInfo.columnKey === 'collar_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.collar_name}</div>,
    },
    {
      title: 'collar Mesh Name',
      dataIndex: 'collar_mesh_name',
      key: 'collar_mesh_name',
      filters: Array.from(new Set(collars.map((s) => s.collar_mesh_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.collar_mesh_name || null,
      onFilter: (value, record) => record.collar_mesh_name.includes(value as string),
      sorter: (a, b) => a.collar_mesh_name.localeCompare(b.collar_mesh_name),
      sortOrder: sortedInfo.columnKey === 'collar_mesh_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.collar_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'collar_image',
      key: 'collar_image',
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.collar_image}
            alt="collar"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Collars) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.collar_status}
                onChange={() => handleStatusToggle(record.collar_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this collar?"
              onConfirm={() => handleDelete(record.collar_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View collar</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add collar 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Collars>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="collar_id"
          dataSource={collars.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No collar found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, collars.length)} of{' '}
                {collars.length}
              </span>
              <Pagination
                {...pagination}
                total={collars.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <CollarModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        collars={collars}
      />
    </>
  );
};

export default CollarsPage;